/**
 * Message Translation Service
 * Handles translation of messages based on user's preferred language
 */

import { Message } from '@/types/messaging';
import { functions } from '@/firebase/config';
import { httpsCallable } from 'firebase/functions';

interface TranslateMessageParams {
  text: string;
  targetLanguage: string;
  sourceLanguage?: string;
}

interface TranslateMessageResponse {
  success: boolean;
  translatedText?: string;
  error?: string;
}

/**
 * Translate a single message text
 */
export async function translateMessageText(
  text: string,
  targetLanguage: string,
  sourceLanguage: string = 'en'
): Promise<string> {
  if (!text || targetLanguage === 'en' || targetLanguage === sourceLanguage) {
    return text;
  }

  try {
    // Call Cloud Function for translation
    const translateMessage = httpsCallable<TranslateMessageParams, TranslateMessageResponse>(
      functions,
      'translateMessage'
    );
    
    const result = await translateMessage({
      text,
      targetLanguage,
      sourceLanguage,
    });

    if (result.data.success && result.data.translatedText) {
      return result.data.translatedText;
    }
  } catch (error) {
    console.error('Error translating message:', error);
  }

  // Return original text if translation fails
  return text;
}

/**
 * Get user's preferred language from user object
 */
export function getUserPreferredLanguage(user: { preferredLanguage?: string } | null | undefined): string {
  return user?.preferredLanguage === 'es' ? 'es' : 'en';
}

/**
 * Get display text for a message (translated if needed)
 * Returns the message text or transcription text, translated if user prefers Spanish
 */
export async function getMessageDisplayText(
  message: Message,
  userPreferredLanguage: string
): Promise<string> {
  // If message has transcription, use that; otherwise use text
  const sourceText = message.transcription?.originalText || message.text || '';
  
  if (!sourceText) return '';
  
  // If user prefers English or source is already in preferred language, return as-is
  if (userPreferredLanguage === 'en' || !message.transcription) {
    // Only translate text messages if user prefers Spanish
    if (userPreferredLanguage === 'es' && !message.transcription) {
      return await translateMessageText(sourceText, 'es', 'en');
    }
    return sourceText;
  }

  // For transcriptions, check if translation exists
  if (message.transcription) {
    // If transcription has translatedText and it matches user's preferred language, use it
    if (
      message.transcription.translatedText &&
      message.transcription.targetLanguage === userPreferredLanguage
    ) {
      return message.transcription.translatedText;
    }

    // Otherwise, translate on the fly
    if (userPreferredLanguage === 'es') {
      return await translateMessageText(
        message.transcription.originalText,
        'es',
        message.transcription.sourceLanguage || 'en'
      );
    }
  }

  return sourceText;
}

